

import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

export default function Services() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI-Powered Web Development Services",
    "description": "Professional web development services using AI technology for faster, more affordable website creation",
    "provider": {
      "@type": "Organization",
      "name": "NeuraSites AI",
      "url": siteUrl
    },
    "serviceType": "Web Development",
    "offers": [
      {
        "@type": "Offer",
        "name": "Custom Website Development",
        "description": "AI-powered custom websites tailored to your business needs",
        "price": "999",
        "priceCurrency": "USD"
      },
      {
        "@type": "Offer",
        "name": "E-commerce Solutions",
        "description": "Complete online stores with payment integration",
        "price": "1499",
        "priceCurrency": "USD"
      }
    ]
  };

  const services = [
    {
      id: 'website-development',
      icon: 'ri-code-s-slash-line',
      title: 'Custom Website Development',
      shortDescription: 'AI-powered custom websites tailored to your business needs',
      price: 'Starting at $999',
      features: [
        'Responsive design for all devices',
        'AI-optimized user experience',
        'Fast loading speeds',
        'SEO-ready structure',
        'Content management system',
        'Contact forms integration'
      ],
      deliveryTime: '24-48 hours',
      image: '/site-images/website-development-hero.webp'
    },
    {
      id: 'ecommerce',
      icon: 'ri-shopping-cart-line',
      title: 'E-commerce Solutions',
      shortDescription: 'Complete online stores with payment integration',
      price: 'Starting at $1,499',
      features: [
        'Product catalog management',
        'Secure payment processing',
        'Inventory management',
        'Order tracking system',
        'Customer accounts',
        'Mobile-optimized checkout'
      ],
      deliveryTime: '3-5 days',
      image: '/site-images/e-commerce-hero.webp'
    },
    {
      id: 'seo',
      icon: 'ri-search-eye-line',
      title: 'SEO Optimization',
      shortDescription: 'Get found on Google with our SEO expertise',
      price: 'Starting at $499',
      features: [
        'Keyword research & strategy',
        'On-page optimization',
        'Technical SEO audit',
        'Local SEO setup',
        'Google Analytics integration',
        'Monthly performance reports'
      ],
      deliveryTime: '1-2 weeks',
      image: '/site-images/seo-optimization-hero.webp'
    },
    {
      id: 'maintenance',
      icon: 'ri-tools-line',
      title: 'Website Maintenance',
      shortDescription: 'Keep your website secure, fast, and up-to-date',
      price: 'Starting at $199/month',
      features: [
        'Security updates & monitoring',
        'Performance optimization',
        'Content updates',
        'Backup management',
        'Uptime monitoring',
        'Priority support'
      ],
      deliveryTime: 'Ongoing',
      image: '/site-images/website-maintenance-hero.webp'
    },
    {
      id: 'landing-pages',
      icon: 'ri-rocket-line',
      title: 'High-Converting Landing Pages',
      shortDescription: 'Conversion-optimized pages for your campaigns',
      price: 'Starting at $699',
      features: [
        'Conversion-focused design',
        'A/B testing setup',
        'Lead capture forms',
        'Analytics integration',
        'Mobile optimization',
        'Fast deployment'
      ],
      deliveryTime: '12-24 hours',
      image: '/site-images/website-development-hero.webp'
    },
    {
      id: 'saas-websites',
      icon: 'ri-cloud-line',
      title: 'SaaS Website Development',
      shortDescription: 'Professional websites for software companies',
      price: 'Starting at $1,299',
      features: [
        'Feature showcase pages',
        'Pricing table integration',
        'User dashboard mockups',
        'API documentation pages',
        'Customer testimonials',
        'Trial signup flows'
      ],
      deliveryTime: '2-4 days',
      image: '/site-images/saas-development-hero.webp'
    }
  ];

  const addOns = [
    {
      title: 'Premium Hosting & SSL',
      description: 'Fast, secure hosting with SSL certificate',
      price: '$29/month'
    },
    {
      title: 'Additional Pages',
      description: 'Extra pages beyond the base package',
      price: '$199/page'
    },
    {
      title: 'Advanced Animations',
      description: 'Custom animations and micro-interactions',
      price: '$299'
    },
    {
      title: 'Content Writing',
      description: 'Professional copywriting for your pages',
      price: '$99/page'
    },
    {
      title: 'Priority Support',
      description: '24/7 priority support and faster response',
      price: '$99/month'
    },
    {
      title: 'Advanced Integrations',
      description: 'CRM, email marketing, and third-party tools',
      price: '$199-$499'
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="AI-Powered Web Development Services | NeuraSites AI"
        description="Professional web development services using AI technology. Custom websites, e-commerce solutions, SEO optimization, and more. Starting at $999."
        keywords="web development services, AI website development, custom websites, e-commerce development, SEO services, website maintenance, landing pages"
        ogTitle="Professional AI-Powered Web Development Services"
        ogDescription="From custom websites to e-commerce solutions, we leverage AI technology to deliver exceptional results faster and more affordably."
        canonicalUrl={`${siteUrl}/services`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden brand-soft-surface">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('/site-images/homepage-network.webp')` }}
          />
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-900/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            AI-Powered Web
            <span className="block brand-heading-accent">
              Development Services
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-4xl mx-auto">
            From custom websites to e-commerce solutions, we leverage AI technology to deliver 
            exceptional results faster and more affordably than traditional development.
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-600 dark:text-gray-300">
            <div className="flex items-center">
              <i className="ri-time-line text-blue-600 mr-2"></i>
              Delivered in hours, not weeks
            </div>
            <div className="flex items-center">
              <i className="ri-shield-check-line text-green-600 mr-2"></i>
              15 years of experience
            </div>
            <div className="flex items-center">
              <i className="ri-money-dollar-circle-line text-sky-600 mr-2"></i>
              Transparent pricing
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={service.id} className="bg-white dark:bg-gray-700/90 border border-transparent dark:border-gray-600 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1">
                <div className="relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                      <i className={`${service.icon} text-blue-600 text-xl`}></i>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{service.title}</h3>
                    <span className="text-2xl font-bold text-[#2563eb] dark:text-[#25a5eb]">{service.price}</span>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">{service.shortDescription}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">What's Included:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-200">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-300">
                      <i className="ri-time-line mr-1"></i>
                      {service.deliveryTime}
                    </div>
                    <Link
                      to={`/services/${service.id}`}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Popular Add-ons</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Enhance your website with these additional services and features.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {addOns.map((addon, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{addon.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{addon.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-[#2563eb] dark:text-[#25a5eb]">{addon.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 brand-cta-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl brand-cta-copy mb-8">
            Let's discuss your project and create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vibe-check"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Start Your Project
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#0F2A4E] transition-colors whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
